/**
 * CORE-M-06 Ad Review tab body — phase A.
 *
 * Chans's 2026-09-15 rebuild spec §2.6: "Ads that have spent without
 * converting, plus the edge case Core already handles (an ad that would
 * empty its ad group if paused — say so in plain English). Pause / Ignore
 * / Snooze."
 *
 * This ticket does the lift-and-shift. The daily-driver `AdHealthTable`
 * already computes what M-06 asks for:
 *
 *   - `MonitoredAd.is_waste` marks the "spent, converted nothing" case, so
 *     no invented thresholds are needed here. The backend gate is the
 *     truth source; the tab renders what it says.
 *   - `AdMonitorResponse.dark_ad_groups[]` names the ad groups whose live
 *     keywords are winning nothing because every ad is disapproved — the
 *     "empty ad group" edge case, surfaced as a red banner above the
 *     table.
 *   - `AdHealthTable`'s Pause/Enable controls are already gated by
 *     `canWrite(capabilities, 'ad_status_write')` — the server decides,
 *     and no button appears if the server has not said yes.
 *
 * CORE-M-09 adoption
 *
 *   The tab-level summary is now an <EvidenceRow>: the same four G-8
 *   fields (spend, window, conversions, reason) that a reader sees on
 *   any Management row. Per-row adoption inside AdHealthTable stays
 *   pending as CORE-M-06.b — that migration is deeper.
 *
 * What is deliberately not in this PR (phase B, filed as CORE-M-06.b)
 *
 *   - Per-row "would empty its ad group if paused" chip. The read has the
 *     data to compute this by grouping the ads array by `ad_group_id`;
 *     surfacing it as an evidence chip on the row is a follow-up so the
 *     lift-and-shift ships first.
 *   - Ignore / Snooze verbs. The existing table only offers Pause /
 *     Enable; the shared row-verbs primitive lands with CORE-M-06.b.
 *   - Per-row EvidenceRow rendering inside AdHealthTable.
 */
import { TriangleAlert } from 'lucide-react';

import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AdHealthTable } from '@/components/google-ads/AdHealthTable';
import { EvidenceRow } from '@/components/google-ads/EvidenceRow';
import {
  canWrite,
  getAdMonitor,
  type AdMonitorResponse,
} from '@/services/googleAdsManagementService';
import { useTenantStore } from '@/stores/useTenantStore';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { useManagementWindowDays } from './useManagementWindow';
import { AdHealthSummaryTiles } from './AdHealthSummaryTiles';
import {
  useManagementChanges,
  useManagementConversions,
} from './useManagementReads';

interface AdReviewTabProps {
  tenantId?: string | null;
}

// Gap A / CORE-M-12: was `const AD_REVIEW_DAYS = 30`, which is why this tab
// still said "last 30 days" with the page selector on 90 — Art's named
// example. Reads the one window the picker writes.

function useAdReview(tenantId: string | null, windowDays: number) {
  return useQuery<AdMonitorResponse>({
    queryKey: ['ga-ad-monitor', tenantId, windowDays],
    queryFn: () => getAdMonitor(tenantId ?? '', windowDays),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

export function AdReviewTab({ tenantId: tenantIdProp }: AdReviewTabProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;
  const windowDays = useManagementWindowDays();
  const q = useAdReview(tenantId, windowDays);
  // Gap A phase 2b: the six Ad Health tiles moved here from the legacy
  // Management body, where they rendered under all seven category tabs. They
  // summarise the table below, so this is where they belong. `q` already
  // holds the ad-monitor read on the shared query key; these two add the
  // other reads the tiles need, off the same cache the legacy body uses.
  const conversions = useManagementConversions(tenantId ?? '');
  const changes = useManagementChanges(tenantId ?? '');

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No workspace selected</CardTitle>
          <CardDescription>
            Pick a workspace to see its ads that need review.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Reading ad health for {tenantId}...
      </div>
    );
  }

  if (q.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Could not read ad health</CardTitle>
          <CardDescription>
            Core could not reach the Google Ads ad-monitor read for{' '}
            <span className="font-medium text-foreground">{tenantId}</span>.
            Check the Google Ads connection in Settings, under Integrations,
            then reload.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const data = q.data;
  const ads = data?.ads ?? [];
  const darkGroups = data?.dark_ad_groups ?? [];
  const wasteCount = data?.waste_count ?? 0;
  const wasteSpend = data?.waste_spend ?? 0;

  // Dev walk 2026-09-24: the empty line names what this tab checked and how
  // many ads it looked at, so it cannot read like another tab's empty state.
  // $25 is WASTE_SPEND_FLOOR in backend ad_monitor_service.assess(); `ads` is
  // every ad read in the window, not only the flagged ones.
  const noneFlagged =
    ads.length === 0
      ? `No ads came back over the last ${windowDays} days, so there was nothing to check.`
      : `Checked ${ads.length} ad${ads.length === 1 ? '' : 's'} over the last ${windowDays} days for any that spent $25 or more without a conversion. None need action.`;

  // CORE-M-09 tab context: the same four G-8 fields (spend, window,
  // conversions, reason) any Management row would carry, applied at the
  // tab level so a reader sees WHY the tab has open items before opening
  // the table. Every field is required non-empty; the primitive throws in
  // tests if a serializer bug empties one.
  const tabEvidence = {
    spend: `$${wasteSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
    window: `last ${windowDays} days`,
    conversions: '0 conv.',
    reason:
      wasteCount === 0
        ? noneFlagged
        : `${wasteCount} ad${wasteCount === 1 ? '' : 's'} spent without converting over the last ${windowDays} days.`,
  };

  return (
    <div className="space-y-4">
      <AdHealthSummaryTiles
        ads={q.data}
        conversions={conversions.data}
        changes={changes.data}
      />
      {/* CORE-UI-01: dark-mode contrast pass, same pattern as Campaign
          Settings' TargetChip — `--destructive` is a dark red in the dark
          theme (a button fill, not a text colour), so text and tints get
          explicit dark: overrides. Light mode unchanged. */}
      {darkGroups.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 dark:border-red-700/60 dark:bg-red-950/50">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive dark:text-red-200" />
          <div className="text-sm">
            <p className="font-medium">
              {darkGroups.length} ad group{darkGroups.length === 1 ? '' : 's'}
              {' '}have no serving ads
            </p>
            <p className="text-muted-foreground">
              Their keywords are live and can win nothing: {darkGroups.join(', ')}
            </p>
          </div>
        </div>
      )}

      <EvidenceRow
        evidence={tabEvidence}
        additionalChips={
          darkGroups.length > 0
            ? [
                {
                  label: `${darkGroups.length} dark ad group${darkGroups.length === 1 ? '' : 's'}`,
                  tone: 'danger',
                  title:
                    'Ad groups whose live keywords have no serving ads. Their impressions are lost.',
                },
              ]
            : undefined
        }
      />

      <AdHealthTable
        ads={ads}
        customerId={data?.customer_id ?? ''}
        tenantId={tenantId}
        focus="waste"
        canSetStatus={canWrite(data?.capabilities, 'ad_status_write')}
      />
    </div>
  );
}

export default AdReviewTab;
